import { useCallback, useEffect, useState } from 'react';
import { Puzzle, Plus, Trash2, ChevronUp, ChevronDown, Settings, X } from 'lucide-react';
import { api } from '@/services/api';
import type { Widget, SiteWidget } from '@/types';
import { WidgetConfigModal } from './widgets/WidgetConfigModal';
import { getDefaultConfig, getWidgetMeta } from './widgets/WidgetEditorRegistry';

export function WidgetsTab({ siteId, isAdmin }: { siteId: string; isAdmin: boolean }) {
  const [siteWidgets, setSiteWidgets] = useState<SiteWidget[]>([]);
  const [availableWidgets, setAvailableWidgets] = useState<Widget[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingSiteWidget, setEditingSiteWidget] = useState<SiteWidget | null>(null);

  const fetchWidgets = useCallback(async () => {
    try {
      const [sw, all] = await Promise.all([
        api.get<SiteWidget[]>(`/sites/${siteId}/widgets`),
        api.get<Widget[]>('/widgets'),
      ]);
      setSiteWidgets(sw);
      setAvailableWidgets(all);
    } catch {
      /* */
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    void fetchWidgets();
  }, [fetchWidgets]);

  const handleAdd = async (widget: Widget) => {
    try {
      const created = await api.post<SiteWidget>(`/sites/${siteId}/widgets`, {
        widgetId: widget.id,
        config: getDefaultConfig(widget.type),
      });
      setShowAdd(false);
      await fetchWidgets();
      setEditingSiteWidget({ ...created, widget });
    } catch {
      /* */
    }
  };

  const handleToggle = async (swId: string, isActive: boolean) => {
    try {
      await api.patch(`/sites/${siteId}/widgets/${swId}`, { isActive: !isActive });
      await fetchWidgets();
    } catch {
      /* */
    }
  };

  const handleDelete = async (swId: string, name: string) => {
    if (!window.confirm(`Widget "${name}" wirklich entfernen?`)) return;
    try {
      await api.delete(`/sites/${siteId}/widgets/${swId}`);
      await fetchWidgets();
    } catch {
      /* */
    }
  };

  const handleMove = async (swId: string, direction: 'up' | 'down') => {
    const sorted = [...siteWidgets].sort((a, b) => a.position - b.position);
    const idx = sorted.findIndex((sw) => sw.id === swId);
    if (idx < 0) return;
    const newPos = direction === 'up' ? Math.max(0, idx - 1) : idx + 1;
    try {
      await api.patch(`/sites/${siteId}/widgets/${swId}`, { position: newPos });
      await fetchWidgets();
    } catch {
      /* */
    }
  };

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      </div>
    );
  }

  const usedWidgetIds = new Set(siteWidgets.map((sw) => sw.widgetId));
  const unusedWidgets = availableWidgets.filter((w) => !usedWidgetIds.has(w.id));
  const sorted = [...siteWidgets].sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-4">
      {isAdmin && unusedWidgets.length > 0 && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowAdd(!showAdd)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-medium text-white hover:bg-brand-700 transition-colors"
          >
            {showAdd ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {showAdd ? 'Abbrechen' : 'Widget hinzufügen'}
          </button>
        </div>
      )}

      {showAdd && isAdmin && (
        <div className="rounded-xl border border-surface-300 bg-surface-100 p-4">
          <p className="mb-3 text-xs font-medium text-neutral-400">Widget auswählen</p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {unusedWidgets.map((w) => {
              const meta = getWidgetMeta(w.type);
              const Icon = meta.icon;
              return (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => handleAdd(w)}
                  className="group flex items-start gap-3 rounded-lg border border-surface-300 bg-surface-200 p-3 text-left transition-colors hover:border-brand-500 hover:bg-surface-300"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-100">
                    <Icon className={`h-5 w-5 ${meta.color}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-neutral-200">{w.name}</p>
                    {w.description && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-neutral-500">
                        {w.description}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-surface-300 bg-surface-100 py-12">
          <Puzzle className="mb-2 h-8 w-8 text-neutral-600" />
          <p className="text-sm text-neutral-500">Keine Widgets konfiguriert.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {sorted.map((sw, idx) => {
            const type = sw.widget?.type ?? '';
            const meta = getWidgetMeta(type);
            const Icon = meta.icon;
            const name = sw.widget?.name ?? 'Widget';
            return (
              <div
                key={sw.id}
                className="flex flex-col rounded-xl border border-surface-300 bg-surface-100 p-4 transition-colors hover:border-surface-400"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-200">
                    <Icon className={`h-5 w-5 ${meta.color}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-neutral-100">{name}</p>
                        <p className="truncate text-[11px] text-neutral-500">
                          {type || sw.widgetId}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                          sw.isActive
                            ? 'bg-green-900/30 text-green-400'
                            : 'bg-surface-300 text-neutral-500'
                        }`}
                      >
                        {sw.isActive ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                    {sw.widget?.description && (
                      <p className="mt-1 line-clamp-2 text-xs text-neutral-500">
                        {sw.widget.description}
                      </p>
                    )}
                  </div>
                </div>

                {isAdmin && (
                  <div className="mt-4 flex items-center justify-between border-t border-surface-300 pt-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleMove(sw.id, 'up')}
                        disabled={idx === 0}
                        className="rounded p-1 text-neutral-500 hover:bg-surface-300 hover:text-neutral-200 disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label="Nach oben"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMove(sw.id, 'down')}
                        disabled={idx === sorted.length - 1}
                        className="rounded p-1 text-neutral-500 hover:bg-surface-300 hover:text-neutral-200 disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label="Nach unten"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggle(sw.id, sw.isActive)}
                        className={`ml-1 relative h-5 w-9 rounded-full transition-colors ${
                          sw.isActive ? 'bg-brand-600' : 'bg-surface-400'
                        }`}
                        aria-label={sw.isActive ? 'Deaktivieren' : 'Aktivieren'}
                      >
                        <span
                          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                            sw.isActive ? 'left-4' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setEditingSiteWidget(sw)}
                        className="inline-flex items-center gap-1 rounded-lg bg-surface-300 px-2.5 py-1.5 text-xs text-neutral-200 hover:bg-surface-400"
                      >
                        <Settings className="h-3.5 w-3.5" />
                        Konfigurieren
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(sw.id, name)}
                        className="rounded-lg p-1.5 text-neutral-500 hover:bg-red-900/30 hover:text-red-400"
                        aria-label="Löschen"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editingSiteWidget && (
        <WidgetConfigModal
          siteId={siteId}
          siteWidget={editingSiteWidget}
          onClose={() => setEditingSiteWidget(null)}
          onSaved={async () => {
            setEditingSiteWidget(null);
            await fetchWidgets();
          }}
        />
      )}
    </div>
  );
}
